/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LucideIcon } from "lucide-react"
import { CalendarCheckIcon, CalendarPlusIcon, CalendarSyncIcon, CalendarXIcon } from "lucide-react"

import type { ValueOf } from "../../utils/types"
import type { Format } from "./timestamp"
import { Timestamp } from "./timestamp"

/**
 * What the timestamp records about the device or the flag.
 */
const Kind = {
    Created: "created",
    Updated: "updated",
    LastSeen: "lastSeen",
    Removed: "removed"
} as const
type Kind = ValueOf<typeof Kind>

/**
 * The icon that stands for each kind.
 *
 * The four are all calendars so they read as one set; only the mark on the page
 * differs, which is what carries the meaning.
 */
const Icon = {
    [Kind.Created]: CalendarPlusIcon,
    [Kind.Updated]: CalendarSyncIcon,
    [Kind.LastSeen]: CalendarCheckIcon,
    [Kind.Removed]: CalendarXIcon
} as const satisfies Record<Kind, LucideIcon>

interface Props {
    /**
     * What the timestamp records, which picks the icon.
     */
    kind: Kind
    /**
     * ISO timestamp from the API, absent when the event never happened.
     */
    timestamp: string | undefined
    /**
     * Shown at rest; the other format replaces it on hover.
     *
     * @default Format.Relative
     */
    format?: Format
}

/**
 * A timestamp with the icon for its kind in front of it.
 *
 * `data-timestamp="hover-area"` hands the whole pair to the timestamp as its
 * hover area, so pointing at the icon swaps the format too. The attribute is the
 * only way across: class names are hashed per module, so `timestamp.module.css`
 * cannot be selected from here.
 *
 * The pair aligns on the timestamp's baseline, so it lines up with a label beside
 * it. A grid with no baseline-aligned item synthesizes one from its own box, which
 * left the text sitting 3px high next to its `<dt>`. The icon opts out and stays
 * centred on the line.
 *
 * @example
 * <TimestampWithIcon kind="created" timestamp={device.timestampCreated} />
 */
export const TimestampWithIcon = ({ format, kind, timestamp }: Props) => {
    const KindIcon = Icon[kind]

    return (
        <span
            className="inline-grid grid-cols-[min-content_minmax(0,1fr)] items-baseline gap-1"
            data-timestamp="hover-area"
        >
            <KindIcon className="size-4 self-center text-muted-foreground" />
            <Timestamp timestamp={timestamp} format={format} />
        </span>
    )
}
